<template>
  <v-container class="fill-height">
    <v-row
      justify="center"
      align="center"
    >
      <v-col
        cols="12"
        sm="10"
        md="6"
        lg="5"
        xl="4"
      >
        <v-card
          max-width="440px"
          elevation="8"
          style="border-radius: 10px; margin: 0 auto;"
          outlined
        >
          <div class="progress-shell">
            <v-progress-linear
              :value="step === 3 ? 100 : 50"
              color="primary"
              height="4"
              background-opacity="0.12"
            />
          </div>

          <v-window v-model="step">
            <v-window-item :value="1">
              <v-card-text class="pa-10 pa-sm-12">
                <div class="step-head mb-2">
                  <v-sheet
                    class="mx-auto mb-8"
                    width="60"
                    height="60"
                    elevation="0"
                  >
                    <v-img
                      :src="imageSource(get('config').logo)"
                      width="60"
                      height="60"
                      contain
                    ></v-img>
                  </v-sheet>
                  <div class="step-label text-caption grey--text text-uppercase">{{ $t('auth.register.step_label') }}</div>
                  <div class="text-h5 font-weight-bold">{{ $t('auth.register.heading') }}</div>
                </div>

                <div class="intro-copy-block mb-8">
                  <p class="section-copy text-body-2 grey--text text--darken-1">
                    {{ $t('auth.register.intro') }}
                  </p>
                </div>

                <v-form
                  @submit.prevent="registerUser($event)"
                  v-model="valid"
                >
                  <div class="mb-5">
                    <div class="text-body-2 font-weight-medium mb-2">{{ $t('auth.register.email_label') }}</div>
                    <v-text-field
                      :rules="eMailRules"
                      validate-on-blur
                      name="email"
                      required
                      outlined
                      dense
                      :placeholder="$t('auth.register.email_placeholder')"
                      hide-details="auto"
                      style="border-radius: 10px;"
                      />
                  </div>

                  <div class="mb-5">
                    <div class="text-body-2 font-weight-medium mb-2">{{ $t('auth.register.password_label') }}</div>
                    <v-text-field
                      v-model="password"
                      name="password"
                      :rules="passwordRules"
                      :append-icon="showPwIcon ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                      @click:append="showPwIcon = !showPwIcon"
                      :type="showPwIcon ? 'text' : 'password'"
                      required
                      outlined
                      dense
                      placeholder="••••••••"
                      hide-details="auto"
                      loading
                      style="border-radius: 10px;"
                      >
                      <template v-slot:progress>
                        <v-progress-linear
                          :value="progress"
                          :color="color"
                          absolute
                          height="2"
                        />
                      </template>
                    </v-text-field>
                  </div>

                  <v-checkbox
                    class="mt-0 pt-0 mb-6"
                    name="Datenschutz"
                    v-model="datenschutzCheckbox"
                    required
                    :rules="[v => !!v || $t('auth.register.validation.consent_required')]"
                    hide-details="auto"
                    color="primary"
                    dense
                  >
                    <template v-slot:label>
                      <div class="text-body-2">
                        {{ $t('auth.register.privacy_consent') }}
                        <a
                          target="_blank"
                          @click.stop="linkTo('Nutzungsbedingungen')"
                          class="link-accent"
                        >{{ $t('auth.register.agb_link') }}</a>
                        {{ $t('auth.register.agb_agreement') }}
                      </div>
                    </template>
                  </v-checkbox>

                  <v-btn
                    color="primary"
                    width="100%"
                    large
                    type="submit"
                    :disabled="!valid"
                    :loading="loading"
                    elevation="0"
                    class="text-none font-weight-bold"
                  >
                    {{ $t('auth.register.submit') }}
                  </v-btn>
                </v-form>
              </v-card-text>

              <div class="trust-row px-10 px-sm-12 pb-10 pb-sm-12">
                <div class="trust-item">
                  <v-icon small>mdi-lock-outline</v-icon>
                  <span>{{ $t('auth.register.trust_secure') }}</span>
                </div>
                <div class="trust-item">
                  <v-icon small>mdi-shield-check-outline</v-icon>
                  <span>{{ $t('auth.register.trust_private') }}</span>
                </div>
              </div>
            </v-window-item>

            <v-window-item :value="3">
              <v-card-text class="pa-10 pa-sm-12 text-center">
                <div
                  v-if="succesfullCreated"
                  class="result-state state-success"
                >
                  <v-icon
                    size="80"
                    color="success"
                    class="mb-6"
                  >mdi-check-circle-outline</v-icon>
                  <div class="text-h5 font-weight-bold mb-2">{{ $t('auth.register.success_title') }}</div>
                  <p class="text-body-2 grey--text text--darken-1">{{ $t('auth.register.success_message') }}</p>
                </div>

                <div
                  v-else
                  class="result-state state-error"
                >
                  <v-icon
                    size="80"
                    color="error"
                    class="mb-6"
                  >mdi-alert-circle-outline</v-icon>
                  <div class="text-h5 font-weight-bold mb-2">{{ $t('auth.register.error_title') }}</div>
                  <p class="text-body-2 grey--text text--darken-1 mb-6">{{ errorMessage }}</p>
                  <v-btn
                    outlined
                    color="primary"
                    width="100%"
                    @click="step = 1"
                    class="text-none font-weight-bold"
                  >
                    {{ $t('auth.register.error_change_email') }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-window-item>
          </v-window>
        </v-card>

        <div class="text-center mt-4 white--text">
          <span class="text-body-2">{{ $t('auth.register.has_account') }}</span>
          <a
            class="text-body-2 font-weight-bold text-decoration-none white--text ml-2"
            @click="linkTo('')"
            style="cursor: pointer;"
          >{{ $t('auth.register.go_to_login') }}</a>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import APIService from '@/services/api.service';
import AppService from '@/services/app.service';
import { mapGetters } from 'vuex';

export default {
  name: 'registerForm',
  components: {
    response: () => import('@/components/response/response'),
  },
  data() {
    return {
      step: 1,
      loading: false,
      valid: false,
      password: '',
      datenschutzCheckbox: false,
      showPwIcon: false,
      eMailRules: [
        v => !!v || this.$t('auth.register.validation.email_required'),
        v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$t('auth.register.validation.email_invalid')
      ],
      passwordRules: [
        v => !!v || this.$t('auth.register.validation.password_required'),
        v => (v && v.length >= 6) || this.$t('auth.register.validation.password_short')
      ],
      errorMessage: '',
      succesfullCreated: false,
    }
  },

  computed: {
    ...mapGetters(["get"]),
    progress() {
      return Math.min(100, this.password.length * 16.6667)
    },
    color() {
      return ['error', 'warning', 'success'][Math.floor(this.progress / 50)]
    },
  },

  methods: {

    imageSource(item, width, height) {
      return AppService.imageSource(item, width, height);
    },

    registerUser(e) {
      this.loading = true
      var payload = APIService.getFormData(APIService.getForm(e))
      payload.dark_theme = true;

      this.$store.dispatch('post',
        {
          endpoint: '/api/v1/users',
          event: payload,
          header: false,
          message: false
        }
      ).then(() => {
        this.succesfullCreated = true;
        this.loading = false;
        this.step = 3
      }).catch((error) => {
        const { response: { status } } = error;
        this.loading = false;
        if (status === 422) {
          APIService.setResponse({ message: this.$t('auth.register.error.email_taken'), color: "red" });
          this.errorMessage = this.$t('auth.register.error.email_taken');
        }
        else {
          APIService.setResponse({ message: this.$t('auth.register.error.email_server_inactive'), color: "red" });
          this.errorMessage = this.$t('auth.register.error.email_server_inactive');
        }
        this.step = 3;
      })
    },

    linkTo(route) {
      this.$router.push('/' + route)
    },
  },
}
</script>

<style scoped>
.progress-shell {
  overflow: hidden;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.step-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.step-label {
  letter-spacing: 1px;
}



.section-copy {
  margin: 0;
  text-align: center;
}

.intro-copy-block {
  max-width: 48ch;
  margin: 0 auto;
  text-align: center;
}

.trust-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 20px;
  font-size: 13px;
  line-height: 1.4;
  opacity: 0.72;
}

.trust-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}



.result-state {
  min-height: 180px;
  padding: 16px;
  text-align: center;
}

.result-state p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.78;
}

.link-accent {
  color: #00C3FF;
  text-decoration: none;
}

:deep(.v-card__text) {
  word-break: normal;
}

:deep(.v-card__actions) {
  justify-content: center;
}

:deep(.v-input__slot) {
  border-radius: 10px !important;
  box-shadow: none !important;
}

:deep(.v-text-field--outlined fieldset) {
  border-width: 1px;
}

:deep(.v-alert) {
  border-radius: 10px;
}

@media (max-width: 600px) {
  .trust-row {
    gap: 6px 12px;
  }
}
</style>
